import { useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  TextField,
  Typography,
} from "@mui/material";
import { UploadFile as UploadFileIcon } from "@mui/icons-material";
import { myToast } from "../../../libs";
import { apiCreateContact } from "../../../graphQL/addressbook/collections/contacts";
import {
  useContactCategoryOptions,
  addContactCategory,
} from "../../../components/component-contact/useContactCategories";

// RFC-4180-ish CSV parser (quoted fields, escaped quotes, CR/LF/CRLF).
const parseCsv = (text) => {
  const rows = [];
  let row = [];
  let field = "";
  let inQuotes = false;
  const src = String(text || "");
  for (let i = 0; i < src.length; i++) {
    const ch = src[i];
    if (inQuotes) {
      if (ch === '"') {
        if (src[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ",") {
      row.push(field);
      field = "";
    } else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && src[i + 1] === "\n") i++;
      row.push(field);
      field = "";
      if (row.some((c) => String(c).trim() !== "")) rows.push(row);
      row = [];
    } else {
      field += ch;
    }
  }
  row.push(field);
  if (row.some((c) => String(c).trim() !== "")) rows.push(row);
  return rows;
};

const normalizeHeader = (h) => String(h || "").toLowerCase().replace(/[^a-z0-9]/g, "");

const HEADER_MAP = {
  firstname: "firstName",
  first: "firstName",
  givenname: "firstName",
  lastname: "lastName",
  last: "lastName",
  surname: "lastName",
  familyname: "lastName",
  name: "fullName",
  fullname: "fullName",
  contactname: "fullName",
  contact: "fullName",
  email: "email",
  emailaddress: "email",
  workemail: "email",
  phone: "phone",
  phonenumber: "phone",
  mobile: "phone",
  mobilephone: "phone",
  cell: "phone",
  telephone: "phone",
  company: "company",
  companyname: "company",
  organization: "company",
  organisation: "company",
};

const rowsToContacts = (rows) => {
  if (!rows.length) return [];
  const headers = rows[0].map((h) => HEADER_MAP[normalizeHeader(h)] || "");
  return rows
    .slice(1)
    .map((cells) => {
      const rec = {};
      headers.forEach((fieldName, i) => {
        if (fieldName && !rec[fieldName]) rec[fieldName] = String(cells[i] || "").trim();
      });
      let firstName = rec.firstName || "";
      let lastName = rec.lastName || "";
      if (!firstName && !lastName && rec.fullName) {
        const parts = rec.fullName.split(/\s+/).filter(Boolean);
        firstName = parts.shift() || "";
        lastName = parts.join(" ");
      }
      return {
        firstName,
        lastName,
        email: rec.email || "",
        phone: rec.phone || "",
        company: rec.company || "",
      };
    })
    .filter((c) => c.firstName || c.lastName || c.email || c.phone || c.company);
};

const ImportContactsDialog = ({ open, onClose, domain, authorId, onComplete }) => {
  const [category, setCategory] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileInputRef = useRef(null);
  const categoryOptions = useContactCategoryOptions(authorId);

  const reset = () => {
    setCategory("");
    setFile(null);
    setBusy(false);
    setProgress(0);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleClose = () => {
    if (busy) return;
    reset();
    onClose?.();
  };

  const handleFileSelect = (e) => {
    const picked = e.target.files?.[0] || null;
    if (picked) setFile(picked);
  };

  const handleConfirm = async () => {
    const name = String(category || "").trim();
    if (!name || !file) return;
    setBusy(true);
    setProgress(0);
    try {
      const text = await file.text();
      const contacts = rowsToContacts(parseCsv(text));
      if (contacts.length === 0) {
        myToast.error("No contacts found in file");
        setBusy(false);
        return;
      }

      // Existing option → use as-is; new name → persist to the shared
      // category source first so every menu picks it up.
      const categoryValue = categoryOptions.includes(name)
        ? name
        : await addContactCategory({ name });

      let created = 0;
      let failed = 0;
      for (let i = 0; i < contacts.length; i++) {
        const c = contacts[i];
        try {
          await apiCreateContact({
            ...(domain ? { domain } : {}),
            category: categoryValue,
            identity: {
              person: { firstName: c.firstName, lastName: c.lastName },
              company: c.company ? { name: c.company } : {},
            },
            reach: {
              emails: c.email ? [{ type: "work", value: c.email }] : [],
              phones: c.phone ? [{ type: "mobile", value: c.phone }] : [],
              social: [],
              addresses: [],
            },
          });
          created++;
        } catch (err) {
          failed++;
          console.error("Failed to import contact:", err);
        }
        setProgress(Math.round(((i + 1) / contacts.length) * 100));
      }

      if (created > 0) {
        myToast.success(`Imported ${created} contact${created === 1 ? "" : "s"}${failed ? `, ${failed} failed` : ""}`);
      } else {
        myToast.error("Import failed for all rows");
      }
      onComplete?.();
      reset();
      onClose?.();
    } catch (err) {
      setBusy(false);
      setProgress(0);
      myToast.error(err?.message || err?.graphQLErrors?.[0]?.message || "Import failed");
    }
  };

  return (
    <Dialog
      open={Boolean(open)}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          m: 0,
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "calc(100% - 24px)", sm: 560 },
          maxWidth: "560px",
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>Import Contacts</DialogTitle>
      <DialogContent sx={{ pt: "8px !important" }}>
        <Autocomplete
          freeSolo
          size="small"
          options={categoryOptions}
          inputValue={category}
          onInputChange={(e, value) => setCategory(value || "")}
          disabled={busy}
          renderInput={(params) => (
            <TextField {...params} label="Contact Type" required sx={{ mt: 0.5 }} />
          )}
        />

        <Box sx={{ mt: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
          <input
            ref={fileInputRef}
            id="import-contacts-dialog-input"
            type="file"
            hidden
            accept=".csv"
            onChange={handleFileSelect}
          />
          <Button
            variant="outlined"
            size="small"
            startIcon={<UploadFileIcon />}
            onClick={() => fileInputRef.current?.click()}
            disabled={busy}
          >
            Choose File
          </Button>
          {file ? (
            <Typography sx={{ fontSize: "0.78rem", color: "#64748b" }}>{file.name}</Typography>
          ) : null}
        </Box>

        {busy ? <LinearProgress variant="determinate" value={progress} sx={{ mt: 1.5 }} /> : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={busy || !String(category || "").trim() || !file}
        >
          Import
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImportContactsDialog;
